"use client";

import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { SentIcon, CodeIcon, AnalyticsUpIcon, SparklesIcon } from "@hugeicons/core-free-icons";
import Link from "next/link";
import { Button, Chip } from "@heroui/react";

// Magnetic effect wrapper for interactive buttons
function Magnetic({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = clientX - (left + width / 2);
    const y = clientY - (top + height / 2);
    setPosition({ x: x * 0.35, y: y * 0.35 });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
      className="w-full sm:w-auto"
    >
      {children}
    </motion.div>
  );
}

export default function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);

  // Detect and track theme mode changes
  React.useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  
  // Track mouse coordinates normalized (-0.5 to 0.5) for parallax feel
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set(clientX / innerWidth - 0.5);
    mouseY.set(clientY / innerHeight - 0.5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Spring animations for a smoother inertial tracking effect
  const springX = useSpring(mouseX, { stiffness: 80, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 20 });

  // Map mouse positions to different layers of parallax translations
  const bgTranslateX = useTransform(springX, [-0.5, 0.5], [-30, 30]);
  const bgTranslateY = useTransform(springY, [-0.5, 0.5], [-30, 30]);

  const leftCardX = useTransform(springX, [-0.5, 0.5], [20, -20]);
  const leftCardY = useTransform(springY, [-0.5, 0.5], [20, -20]);

  const rightCardX = useTransform(springX, [-0.5, 0.5], [-20, 20]);
  const rightCardY = useTransform(springY, [-0.5, 0.5], [-20, 20]);

  // Define dynamic shifting background colors for the mesh circles
  const meshColors1 = isDark
    ? [
        "rgba(30, 58, 138, 0.25)", // Indigo-900
        "rgba(88, 28, 135, 0.25)", // Purple-900
        "rgba(15, 118, 110, 0.25)", // Teal-900
        "rgba(30, 58, 138, 0.25)", 
      ]
    : [
        "rgba(59, 130, 246, 0.15)", // Blue-500
        "rgba(139, 92, 246, 0.15)", // Violet-500
        "rgba(20, 184, 166, 0.15)", // Teal-500
        "rgba(59, 130, 246, 0.15)",
      ];

  const meshColors2 = isDark
    ? [
        "rgba(76, 29, 149, 0.2)",  // Violet-950
        "rgba(124, 58, 237, 0.15)", // Purple-600
        "rgba(99, 102, 241, 0.2)",  // Indigo-500
        "rgba(76, 29, 149, 0.2)",
      ]
    : [
        "rgba(168, 85, 247, 0.15)", // Purple-500
        "rgba(236, 72, 153, 0.15)", // Pink-500
        "rgba(99, 102, 241, 0.15)", // Indigo-500
        "rgba(168, 85, 247, 0.15)",
      ];

  return (
    <div 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative flex min-h-screen min-w-full items-center justify-center overflow-hidden px-4 md:px-8 lg:px-16 pt-28 pb-16 bg-neutral-50 dark:bg-black transition-colors duration-500"
    >
      
      {/* Background Gradients & Mesh Shapes */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Dynamic mesh circles that react to mouse position and animate color */}
        <motion.div
          style={{ x: bgTranslateX, y: bgTranslateY }}
          className="absolute -top-[10%] left-[10%] h-[500px] w-[500px] rounded-full blur-[120px]"
          animate={{
            scale: [1, 1.05, 0.95, 1],
            backgroundColor: meshColors1,
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          style={{ x: useTransform(springX, [-0.5, 0.5], [40, -40]), y: useTransform(springY, [-0.5, 0.5], [40, -40]) }}
          className="absolute top-[30%] left-[45%] h-[600px] w-[600px] rounded-full blur-[130px]"
          animate={{
            scale: [1, 0.95, 1.05, 1],
            backgroundColor: meshColors2,
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Glassmorphic grid lines */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      {/* Main Container */}
      <div className="mx-auto flex flex-col items-center justify-center max-w-5xl z-10 w-full text-center space-y-12 relative">
        
        {/* Floating Code Card (Left) */}
        <motion.div
          style={{ x: leftCardX, y: leftCardY }}
          initial={{ opacity: 0, scale: 0.8, x: -60, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, x: 0, rotate: -3 }}
          transition={{ delay: 0.8, type: "spring", stiffness: 100, damping: 15 }}
          className="absolute left-[-10%] top-[10%] hidden xl:flex flex-col items-start p-4 rounded-2xl bg-white/70 dark:bg-neutral-900/40 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-800/40 shadow-2xl w-60 text-left font-outfit select-none pointer-events-none transition-colors duration-500"
        >
          <div className="flex gap-1.5 mb-3">
            <div className="w-2 h-2 rounded-full bg-red-500/80" />
            <div className="w-2 h-2 rounded-full bg-yellow-500/80" />
            <div className="w-2 h-2 rounded-full bg-green-500/80" />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <HugeiconsIcon icon={CodeIcon} className="text-blue-600 dark:text-blue-500 w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">clean_code.ts</span>
          </div>
          <div className="text-[11px] font-mono text-neutral-600 dark:text-neutral-300 space-y-1">
            <p className="text-blue-600 dark:text-blue-400">const<span className="text-neutral-800 dark:text-white"> agency = </span>{"{"}</p>
            <p className="pl-3 text-purple-600 dark:text-purple-400">tech:<span className="text-green-600 dark:text-green-400"> &quot;Next.js&quot;</span>,</p>
            <p className="pl-3 text-purple-600 dark:text-purple-400">speed:<span className="text-green-600 dark:text-green-400"> &quot;100%&quot;</span>,</p>
            <p className="pl-3 text-purple-600 dark:text-purple-400">aiPowered:<span className="text-amber-600 dark:text-yellow-400"> true</span></p>
            <p className="text-blue-600 dark:text-blue-400">{"};"}</p>
          </div>
        </motion.div>

        {/* Floating Performance Card (Right) */}
        <motion.div
          style={{ x: rightCardX, y: rightCardY }}
          initial={{ opacity: 0, scale: 0.8, x: 60, rotate: 5 }}
          animate={{ opacity: 1, scale: 1, x: 0, rotate: 3 }}
          transition={{ delay: 1, type: "spring", stiffness: 100, damping: 15 }}
          className="absolute right-[-10%] bottom-[10%] hidden xl:flex flex-col items-start p-4 rounded-2xl bg-white/70 dark:bg-neutral-900/40 backdrop-blur-xl border border-neutral-200/50 dark:border-neutral-800/40 shadow-2xl w-60 text-left font-outfit select-none pointer-events-none transition-colors duration-500"
        >
          <div className="flex items-center justify-between w-full mb-3">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={AnalyticsUpIcon} className="text-indigo-600 dark:text-indigo-500 w-4 h-4" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">Performance</span>
            </div>
            <span className="text-[10px] font-bold text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">100%</span>
          </div>
          <div className="w-full space-y-2">
            <div className="h-1.5 w-full bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "98%" }}
                transition={{ delay: 1.5, duration: 1.2, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
              />
            </div>
            <div className="flex justify-between text-[9px] text-neutral-500 dark:text-neutral-400">
              <span>Lighthouse Score</span>
              <span className="font-bold text-neutral-800 dark:text-neutral-250">98%</span>
            </div>
          </div>
        </motion.div>

        {/* Text & CTAs Section */}
        <div className="flex flex-col items-center space-y-6 max-w-4xl">
          
          {/* Animated Chip badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="inline-flex justify-center"
          >
            <Chip
              variant="primary"
              className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 text-blue-600 dark:text-blue-300 font-outfit text-sm font-semibold py-1.5 px-3 rounded-full backdrop-blur-md transition-colors duration-500"
            >
              <HugeiconsIcon icon={SparklesIcon} className="text-yellow-500 animate-pulse w-4 h-4 mr-1" />
              <Chip.Label>Leading AI-Powered Web Agency</Chip.Label>
            </Chip>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-recoleta leading-[1.05] tracking-tight text-center text-neutral-900 dark:text-white">
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 100 }}
              className="inline-block"
            >
              Web Development{" "}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35, type: "spring", stiffness: 100 }}
              className="inline-block text-blue-600 dark:text-blue-400"
            >
              Company
            </motion.span>
          </h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
            className="font-manjari text-lg sm:text-xl text-neutral-600 dark:text-neutral-300 max-w-2xl font-light leading-relaxed mx-auto text-center"
          >
            Specializing in modern, fast, and intuitive web solutions, we deliver
            a seamless user experience by combining clean design with powerful,
            efficient, and future-proof technology.
          </motion.p>

          {/* Call to Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center pt-2"
          >
            <Magnetic>
              <Link href="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full py-4 sm:w-auto font-semibold font-outfit text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-500 hover:via-indigo-500 hover:to-violet-500 shadow-lg shadow-indigo-500/30 border-none transition-all duration-300 transform active:translate-y-0 cursor-pointer"
                >
                  Book Free Consultation
                  <HugeiconsIcon icon={SentIcon} className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </Magnetic>
            
            <Magnetic>
              <Link href="/services" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full py-4 sm:w-auto font-semibold font-outfit border-neutral-300 dark:border-neutral-800 text-neutral-800 dark:text-neutral-200 bg-white/80 dark:bg-neutral-900/30 backdrop-blur-md hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all duration-300 transform active:translate-y-0 cursor-pointer"
                >
                  Our Services
                  <HugeiconsIcon icon={CodeIcon} className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </Magnetic>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

